import PropTypes from 'prop-types';
import { trans } from '@spotahome/soyuz/client';

import { LandlordScoringShape } from '@spotahome/ui-library/src/tenant/constants/shapes';
import { DSIcon } from '@spotahome/ui-library/src/base';
import { yearsSinceDate } from '@spotahome/ui-library/src/tenant/components/utils/dateUtils';
import PrivateLandlord from '@spotahome/ui-library/src/tenant/components/icons/PrivateLandlord';
import ProfessionalLandlord from '@spotahome/ui-library/src/tenant/components/icons/ProfessionalLandlord';
import Scoring from '@spotahome/ui-library/src/tenant/components/Scoring';
import Tag from '@spotahome/ui-library/src/tenant/components/Tag';
import {
  PRIVATE_LANDLORD,
  PROFESSIONAL_LANDLORD
} from '@spotahome/ui-library/src/tenant/components/ExtendedLandlordInformationContent/constants';

import { useLandlordInformation } from '../../context/LandlordInformationContext';
import { ListingSectionObservable } from '../ListingSection';

import './MeetYourLandlord.scss';

const MAIN_LANGUAGES = [
  'spanish',
  'english',
  'portuguese',
  'french',
  'german',
  'italian'
];

const EMPTY_ARRAY = [];
const EMPTY_OBJECT = {};

const LANDLORD_ICONS = {
  [PRIVATE_LANDLORD]: () => <PrivateLandlord color="blue" />,
  [PROFESSIONAL_LANDLORD]: () => <ProfessionalLandlord color="blue" />
};

const MeetYourLandlord = ({ landlordScoring = EMPTY_OBJECT }) => {
  const { landlordInformation } = useLandlordInformation();

  if (!landlordInformation) {
    return null;
  }

  const {
    type,
    firstListingDate: joiningDate,
    listingsPublished,
    averageResponseTime,
    languages = EMPTY_ARRAY
  } = landlordInformation;

  const timeWithSpotahome = joiningDate ? yearsSinceDate(joiningDate) : null;
  const hasScoring =
    landlordScoring?.score != null &&
    landlordScoring?.totalBookingsClosed != null;

  const filteredCommonLanguages = languages.filter(language =>
    MAIN_LANGUAGES.includes(language)
  );
  const spokenLanguagesTranslated = filteredCommonLanguages
    .map(language => trans(`landlord_language_${language}`))
    .join(', ');

  const tagItems = [
    {
      show: averageResponseTime != null,
      icon: 'quick_phrases',
      label: trans('landlord.info.responds.within', {
        time: averageResponseTime
      })
    },
    {
      show: timeWithSpotahome != null,
      icon: 'furnished',
      label: trans('landlord.info.years.with.spotahome', {
        years: timeWithSpotahome
      })
    },
    {
      show: listingsPublished != null,
      icon: 'add_ad',
      label: `${trans(
        'landlord.info.listings.on.spotahome'
      )} ${listingsPublished}`
    },
    {
      show: true,
      icon: 'emoji_language',
      label: trans('landlord.info.speaks', {
        languages:
          spokenLanguagesTranslated || trans('landlord_language_english')
      })
    }
  ];

  const tagsToShow = tagItems.filter(tag => tag.show);

  return (
    <ListingSectionObservable
      section="aboutLandlord"
      className="meet-your-landlord"
    >
      <div className="meet-your-landlord__content">
        <hr className="meet-your-landlord__divider" />
        <h2 className="meet-your-landlord__title">
          {trans('listing.meet_your_landlord.title')}
        </h2>
        <div className="meet-your-landlord__header">
          <div className="meet-your-landlord__icon-wrapper">
            {LANDLORD_ICONS[type]?.()}
          </div>
          <div className="meet-your-landlord__title-block">
            <h3 className="meet-your-landlord__type">
              {trans(`listing.landlord_info.type.${type}`)}
            </h3>
            <div className="meet-your-landlord__meta">
              <span className="meet-your-landlord__verified">
                <DSIcon
                  className="meet-your-landlord__verified-icon"
                  name="verified_filled"
                  size="sm"
                />
                {trans('listing.landlord_info.verified')}
              </span>
              {hasScoring && (
                <Scoring
                  score={landlordScoring.score}
                  totalReviews={landlordScoring.totalBookingsClosed}
                  size="medium"
                  bold={false}
                  tooltipPosition="top"
                  iconClassName="meet-your-landlord__scoring-icon"
                  className="meet-your-landlord__scoring-text"
                />
              )}
            </div>
          </div>
        </div>

        <div className="meet-your-landlord__chips">
          {tagsToShow.map(tag => (
            <Tag
              key={tag.icon}
              label={tag.label}
              className="meet-your-landlord__chip"
              renderLeadingIcon={() => <DSIcon name={tag.icon} size="sm" />}
              isSpaced
              size="big"
              colorSchema="light"
            />
          ))}
        </div>
      </div>
    </ListingSectionObservable>
  );
};

MeetYourLandlord.propTypes = {
  landlordScoring: PropTypes.shape(LandlordScoringShape)
};

export default MeetYourLandlord;
